// src/services/emailScraper.service.js
// Frontend service for Gmail OAuth and email scraping

import { supabase } from '../utils/supabase';

// Which edge function runs a scan.
//
// CUTOVER COMPLETE (2026-08-14). One function now serves every provider:
// v2 reads the provider off the connection row and picks its own mail
// source, so the client no longer resolves it. Verified in production
// in this order — Gmail auto-scan (push), Gmail manual scans,
// Gmail-over-IMAP (X-GM-RAW), then RFC 3501 IMAP against iCloud, each
// with real orders imported complete with items, totals and images.
//
// This replaced a client-side provider lookup that cost a database round
// trip on every scan just to choose between two functions.
//
// Rollback needs no code change: set REACT_APP_SCRAPER_FN in the Netlify
// environment and redeploy — 'scrape-order-emails' for Gmail or
// 'scrape-order-emails-imap' for IMAP. Both remain deployed for that
// purpose. The override wins for every provider, which is also how the
// rewrite was tested locally throughout.
export const SCRAPER_FUNCTION_OVERRIDE = process.env.REACT_APP_SCRAPER_FN || null;

/**
 * A valid access token for an edge-function call.
 *
 * Every call site here used to run `supabase.auth.refreshSession()`
 * unconditionally. For one-shot operations that was merely wasteful; for
 * scrapeOrderEmailsBatch it was destructive, because a scan PAGINATES —
 * one call per batchSize (10) emails — so a 1,300-email scan forced ~130
 * refreshes in a few minutes.
 *
 * Supabase ROTATES refresh tokens: each refresh mints a new one and
 * revokes the old. Hammering it that hard means overlapping refreshes
 * race, one of them presents a token that was just revoked, the refresh
 * fails, and supabase-js emits SIGNED_OUT — which is why a long scan
 * ended with the user logged out. Measured on a real 12-minute scan: 324
 * refresh tokens issued, 322 revoked, peaking at 49 in one minute against
 * a session that should refresh about once an hour.
 *
 * getSession() already refreshes an expired token on its own. We only
 * force one when expiry is close enough that a slow batch could cross it
 * mid-flight, since the edge relay rejects an expired JWT before the
 * function ever runs.
 */
const TOKEN_REFRESH_MARGIN_MS = 120_000;

/**
 * Emails per round trip.
 *
 * Was 10, which meant a 3,240-email scan made 324 calls to the edge
 * function and spent 15 minutes doing it -- 2.84s per batch, most of it
 * plausibly per-invocation overhead (auth, lock, settings, connection,
 * token) rather than per-email work. The function clamps this to 100 and
 * now reports per-stage timings, so the next real scan will say how much
 * of that 2.84s was fixed and whether this should go higher or back down.
 */
const DEFAULT_SCAN_BATCH_SIZE = 40;

const getFunctionAccessToken = async () => {
  const { data: sessionData } = await supabase.auth.getSession();
  let session = sessionData?.session ?? null;

  const expiresAtMs = session?.expires_at ? session.expires_at * 1000 : 0;
  const expiringSoon = !expiresAtMs || (expiresAtMs - Date.now()) < TOKEN_REFRESH_MARGIN_MS;

  if (!session || expiringSoon) {
    const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
    if (!refreshError && refreshData?.session) session = refreshData.session;
  }

  if (!session?.access_token) {
    throw new Error('Not authenticated - please sign in again');
  }
  return session;
};

const SCRAPER_FN = 'scrape-order-emails-v2';

/**
 * Pull the real message out of a failed functions.invoke().
 *
 * supabase-js reports EVERY non-2xx as a FunctionsHttpError whose
 * `message` is the fixed string "Edge Function returned a non-2xx status
 * code" — the function's own JSON body is on `error.context`, an unread
 * Response. So the message a user sees is the same regardless of whether
 * they hit a quota, a bad password, or a dead worker, and any code
 * branching on the text (this file had five such branches) can never
 * match.
 *
 * Returns the function's `error` field, or null when the body isn't
 * readable — callers fall back to their own default.
 */
const extractFunctionError = async (invokeError) => {
  try {
    const body = await invokeError?.context?.json?.();
    return body?.error || body?.message || null;
  } catch {
    // Body already consumed, empty, or not JSON (a crashed worker
    // returns plain text). Nothing to add.
    return null;
  }
};

/**
 * The browser's IANA timezone (e.g. "America/Chicago"), sent with scan
 * requests so the backend can derive order dates in the user's local time
 * rather than the sender's. Returns null if unavailable, in which case the
 * backend keeps its previous sender-local behavior.
 */
const getBrowserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
};

// Tier-specific weekly limits for the unified Orders & Tracking quota.
// Phase 4C cleanup (2026-05-30): legacy 4-tier values (starter 100,
// pro 250) dropped, free went from 0 to 25 (Free is now allowed,
// quota-gated). Plus / Lifetime unlimited.
//
// In practice the frontend doesn't read these — the edge functions own
// the limit check via WEEKLY_ORDER_LIMITS in their own files. Kept here
// only for any orphan caller that still imports getWeeklyOrderLimit.
export const WEEKLY_ORDER_LIMITS = {
  free: 25,
  pro: Infinity,
};

export const getWeeklyOrderLimit = (tier) => {
  return WEEKLY_ORDER_LIMITS[tier] ?? WEEKLY_ORDER_LIMITS.pro;
};

// Supported stores for filtering (must match backend SUPPORTED_STORES)
// Supported stores. `category` drives the grouping/divider rendering in
// the scrape modal — values: 'retail', 'sneakers'. (Collectibles like
// Topps/Pokemon Center/GameStop stay in 'retail' for now since adding
// a third group makes the modal feel busy at current column width.)
// Within a category the modal sorts alphabetically by name.
export const SUPPORTED_STORES = [
  // Retail
  { id: 'Amazon', name: 'Amazon', domain: 'amazon.com', category: 'retail' },
  { id: 'Apple', name: 'Apple', domain: 'apple.com', category: 'retail' },
  { id: 'Best Buy', name: 'Best Buy', domain: 'bestbuy.com', category: 'retail' },
  { id: 'Costco', name: 'Costco', domain: 'costco.com', category: 'retail' },
  { id: 'Five Below', name: 'Five Below', domain: 'fivebelow.com', category: 'retail' },
  { id: 'GameStop', name: 'GameStop', domain: 'gamestop.com', category: 'retail' },
  { id: 'Home Depot', name: 'Home Depot', domain: 'homedepot.com', category: 'retail' },
  { id: 'LEGO', name: 'LEGO', domain: 'lego.com', category: 'retail' },
  { id: "Lowe's", name: "Lowe's", domain: 'lowes.com', category: 'retail' },
  { id: "Macy's", name: "Macy's", domain: 'macys.com', category: 'retail' },
  { id: 'Menards', name: 'Menards', domain: 'menards.com', category: 'retail' },
  { id: 'Pokemon Center', name: 'Pokemon Center', domain: 'pokemon.com', category: 'retail' },
  { id: 'PlayStation Direct', name: 'PlayStation Direct', domain: 'direct.playstation.com', category: 'retail' },
  { id: 'Secret Lair', name: 'Secret Lair', domain: 'secretlair.wizards.com', category: 'retail' },
  { id: "Sam's Club", name: "Sam's Club", domain: 'samsclub.com', category: 'retail' },
  { id: 'Scheels', name: 'Scheels', domain: 'scheels.com', category: 'retail' },
  { id: 'Target', name: 'Target', domain: 'target.com', category: 'retail' },
  { id: 'Topps', name: 'Topps', domain: 'topps.com', category: 'retail' },
  { id: 'Walmart', name: 'Walmart', domain: 'walmart.com', category: 'retail' },
  // Sneakers
  { id: 'Footsites', name: 'Footsites', domain: 'footlocker.com', category: 'sneakers' },
  { id: 'Hibbett', name: 'Hibbett', domain: 'hibbett.com', category: 'sneakers' },
  { id: 'Nike', name: 'Nike', domain: 'nike.com', category: 'sneakers' },
];

/**
 * Get the user's connected Gmail account (legacy - returns first/primary)
 */
export const getEmailConnection = async (userId) => {
  const { data, error } = await supabase
    .from('user_email_connections')
    .select('*')
    .eq('user_id', userId)
    .order('is_primary', { ascending: false })
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle();

  if (error && error.code !== 'PGRST116') {
    throw error;
  }

  return data;
};

/**
 * Get all connected Gmail accounts for a user
 */
export const getEmailConnections = async (userId) => {
  const { data, error } = await supabase
    .from('user_email_connections')
    .select('*')
    .eq('user_id', userId)
    .order('is_primary', { ascending: false })
    .order('created_at', { ascending: true });

  if (error) {
    throw error;
  }

  return data || [];
};

/**
 * Get ALL connected accounts for a user (Gmail + IMAP)
 */
export const getAllEmailConnections = async (userId) => {
  const { data, error } = await supabase
    .from('user_email_connections')
    .select('*')
    .eq('user_id', userId)
    .order('is_primary', { ascending: false })
    .order('created_at', { ascending: true });

  if (error) {
    throw error;
  }

  return data || [];
};

/**
 * Get a specific email connection by ID
 */
export const getEmailConnectionById = async (connectionId) => {
  const { data, error } = await supabase
    .from('user_email_connections')
    .select('*')
    .eq('id', connectionId)
    .single();

  if (error) {
    throw error;
  }

  return data;
};

/**
 * Initiate Gmail OAuth flow
 * Returns the authorization URL to redirect the user to
 */
export const initiateGmailOAuth = async () => {
  // Edge functions verify the JWT at the relay, before the function
  // runs, so the token has to be valid here.
  const session = await getFunctionAccessToken();

  console.log('OAuth init - using fresh token, expires:', new Date(session.expires_at * 1000).toISOString());

  const response = await supabase.functions.invoke('gmail-oauth-init', {
    body: {},
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
    },
  });

  if (response.error) {
    console.error('OAuth init error:', response.error);
    // Check if it's a relay-level auth error (JWT rejected by Supabase before reaching function)
    const errMsg = response.error?.message || response.error?.context?.message || 'Failed to initiate OAuth';
    throw new Error(errMsg);
  }

  return response.data;
};

/**
 * Disconnect a specific Gmail account by connection ID
 */
export const disconnectGmailById = async (connectionId) => {
  const { error } = await supabase
    .from('user_email_connections')
    .delete()
    .eq('id', connectionId);

  if (error) {
    throw error;
  }

  return true;
};

/**
 * Disconnect Gmail account (legacy - disconnects all for user)
 */
export const disconnectGmail = async (userId) => {
  const { error } = await supabase
    .from('user_email_connections')
    .delete()
    .eq('user_id', userId)
    .eq('provider', 'gmail');

  if (error) {
    throw error;
  }

  return true;
};

/**
 * Set an account as primary
 */
export const setPrimaryAccount = async (userId, connectionId) => {
  // First, unset all as primary
  await supabase
    .from('user_email_connections')
    .update({ is_primary: false })
    .eq('user_id', userId)
    .eq('provider', 'gmail');

  // Then set the selected one as primary
  const { error } = await supabase
    .from('user_email_connections')
    .update({ is_primary: true })
    .eq('id', connectionId);

  if (error) {
    throw error;
  }

  return true;
};

/**
 * Enable Gmail auto-scan (push notifications) for a specific connection.
 * Calls gmail-watch-start which registers a users.watch on Gmail pointing
 * at our Cloud Pub/Sub topic. Returns { ok, historyId, expiresAt }.
 */
export const enableGmailAutoScan = async (connectionId) => {
  const { data, error } = await supabase.functions.invoke('gmail-watch-start', {
    body: { connectionId },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
};

/**
 * Disable Gmail auto-scan for a connection. Stops the Gmail watch and
 * clears the auto_scan_enabled flag.
 */
export const disableGmailAutoScan = async (connectionId) => {
  const { data, error } = await supabase.functions.invoke('gmail-watch-stop', {
    body: { connectionId },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
};

/**
 * Send sample "received / shipped / delivered / cancelled" embeds to the
 * user's configured Discord webhook (or a one-off webhookUrl override).
 * Used by the Preview button in the Discord webhook settings modal.
 */
export const sendPreviewOrderWebhooks = async (webhookUrl) => {
  const { data, error } = await supabase.functions.invoke('preview-order-webhooks', {
    body: webhookUrl ? { webhookUrl } : {},
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
};

/**
 * Update account label
 */
export const updateAccountLabel = async (connectionId, label) => {
  const { error } = await supabase
    .from('user_email_connections')
    .update({ label, updated_at: new Date().toISOString() })
    .eq('id', connectionId);

  if (error) {
    throw error;
  }

  return true;
};

// getScrapeUsage removed in Phase 4C cleanup (2026-05-30). It read the
// legacy weekly_order_usage table, which has been dropped. Frontend
// callers that need a usage count should use the
// useWeeklyOrdersTrackingUsage hook (src/hooks/...), which reads from
// the new orders_tracking_events log via get_combined_weekly_usage.

// Helper: Get next weekly reset time (Sunday midnight UTC)
const getNextWeeklyReset = () => {
  const now = new Date();
  const daysUntilSunday = (7 - now.getUTCDay()) % 7 || 7;
  const nextSunday = new Date(now);
  nextSunday.setUTCDate(now.getUTCDate() + daysUntilSunday);
  nextSunday.setUTCHours(0, 0, 0, 0);
  return nextSunday.toISOString();
};

// Scan modes for email scraping
export const SCAN_MODES = {
  ORDERS: 'orders',     // Order confirmations and cancellations only
  SHIPPING: 'shipping', // Shipping/delivery updates only (for existing orders)
  BOTH: 'both',         // Both orders and shipping (default)
};

/**
 * Scrape order emails from connected Gmail (single batch)
 * @param {Object} options - Scrape options
 * @param {string} options.startDate - Start date (YYYY-MM-DD)
 * @param {string} options.endDate - End date (YYYY-MM-DD)
 * @param {string[]} [options.stores] - Optional store filter
 * @param {number} [options.batchSize] - Emails per batch (default 10)
 * @param {string} [options.cursor] - Pagination cursor from previous call
 * @param {string} [options.connectionId] - Specific email connection to use (for multi-account)
 * @param {boolean} [options.autoFetchTracking] - Auto-fetch shipping/delivery emails (default true)
 * @param {string} [options.scanMode] - Scan mode: 'orders', 'shipping', or 'both' (default 'both')
 * @param {string[]} [options.orderNumbers] - For 'shipping' mode: specific order numbers to check
 */
export const scrapeOrderEmailsBatch = async ({ startDate, endDate, stores, batchSize = DEFAULT_SCAN_BATCH_SIZE, cursor, connectionId, autoFetchTracking = true, scanMode = 'both', orderNumbers }) => {
  // See getFunctionAccessToken: refreshing on EVERY batch is what logged
  // users out at the end of long scans.
  const session = await getFunctionAccessToken();

  // No provider routing: one function serves Gmail and IMAP, and it
  // resolves the provider server-side from the connection row.
  const functionName = SCRAPER_FUNCTION_OVERRIDE || SCRAPER_FN;
  if (SCRAPER_FUNCTION_OVERRIDE) {
    console.info(`[scraper] Using override function "${functionName}" (REACT_APP_SCRAPER_FN)`);
  }


  const response = await supabase.functions.invoke(functionName, {
    headers: {
      Authorization: `Bearer ${session.access_token}`,
    },
    body: {
      startDate,
      endDate,
      stores,
      batchSize,
      cursor,
      connectionId,
      autoFetchTracking,
      scanMode,
      orderNumbers,
      // The user's IANA timezone. Order dates are derived from the email's
      // absolute timestamp in THIS zone — many stores timestamp in UTC, so
      // an order placed at 9pm local was being recorded as the next day.
      timeZone: getBrowserTimeZone(),
    },
  });

  if (response.error) {
    // Classify on the FUNCTION's message, not the invoke wrapper's — the
    // wrapper's text is identical for every failure, so matching against
    // it silently classified nothing.
    const detail = await extractFunctionError(response.error);
    const message = detail || response.error.message || 'Failed to scrape emails';
    const error = new Error(message);
    if (message.includes('limit reached')) {
      error.code = 'LIMIT_REACHED';
      error.usage = response.data?.usage;
    } else if (message.includes('not connected')) {
      error.code = 'NOT_CONNECTED';
    } else if (message.includes('reconnect')) {
      error.code = 'TOKEN_EXPIRED';
    } else if (message.includes('PERMISSION_ERROR') || message.includes('Insufficient permissions')) {
      error.code = 'PERMISSION_ERROR';
      error.message = 'Insufficient permissions to read emails. Please disconnect your Gmail account and reconnect it, making sure to allow email read access.';
    } else if (message.includes('Scrape in progress') || message.includes('already running')) {
      error.code = 'SCRAPE_IN_PROGRESS';
      error.message = 'A scan is already in progress. Please wait for it to complete before starting a new one.';
    }
    throw error;
  }

  return response.data;
};

/**
 * Scrape all order emails with cursor pagination loop
 * @param {Object} options - Scrape options
 * @param {string} options.startDate - Start date (YYYY-MM-DD)
 * @param {string} options.endDate - End date (YYYY-MM-DD)
 * @param {string[]} [options.stores] - Optional store filter
 * @param {number} [options.batchSize] - Emails per batch (default 10)
 * @param {string} [options.connectionId] - Specific email connection to use
 * @param {boolean} [options.autoFetchTracking] - Auto-fetch shipping/delivery emails (default true)
 * @param {string} [options.scanMode] - Scan mode: 'orders', 'shipping', or 'both' (default 'both')
 * @param {string[]} [options.orderNumbers] - For 'shipping' mode: specific order numbers to check
 * @param {function} [options.onProgress] - Progress callback (processed, created, updated)
 */
export const scrapeOrderEmails = async ({ startDate, endDate, stores, batchSize = DEFAULT_SCAN_BATCH_SIZE, connectionId, autoFetchTracking = true, scanMode = 'both', orderNumbers, onProgress }) => {
  const aggregatedResults = {
    success: true,
    // Backend naming
    processed: 0,
    created: 0,
    updated: 0,
    skipped: 0,
    skippedDueToLimit: 0,
    // Frontend-compatible naming (for ScrapingContext)
    found: 0,
    alreadyProcessed: 0,
    parsed: 0,
    // Data
    orders: [],
    errors: [],
    usage: null,
    tracking: null, // Stats from auto-tracking fetch
    scanMode: null, // Mode used for this scrape
    limitWarning: null,
  };

  let cursor = null;

  do {
    const result = await scrapeOrderEmailsBatch({
      startDate,
      endDate,
      stores,
      batchSize,
      cursor,
      connectionId,
      autoFetchTracking,
      scanMode,
      orderNumbers,
    });

    // Aggregate results (backend naming)
    aggregatedResults.processed += result.processed || 0;
    aggregatedResults.created += result.created || 0;
    aggregatedResults.updated += result.updated || 0;
    aggregatedResults.skipped += result.skipped || 0;
    aggregatedResults.skippedDueToLimit += result.skippedDueToLimit || 0;
    // Aggregate results (frontend-compatible naming)
    aggregatedResults.found += result.found || 0;
    aggregatedResults.alreadyProcessed += result.alreadyProcessed || 0;
    aggregatedResults.parsed += result.parsed || 0;
    // Data
    if (result.orders) aggregatedResults.orders.push(...result.orders);
    if (result.errors) aggregatedResults.errors.push(...result.errors);
    if (result.usage) aggregatedResults.usage = result.usage;
    if (result.scanMode) aggregatedResults.scanMode = result.scanMode;
    if (result.limitWarning) aggregatedResults.limitWarning = result.limitWarning;

    // Aggregate tracking stats (only present on first batch when autoFetchTracking is true)
    if (result.tracking) {
      if (!aggregatedResults.tracking) {
        aggregatedResults.tracking = { processed: 0, created: 0, updated: 0 };
      }
      aggregatedResults.tracking.processed += result.tracking.processed || 0;
      aggregatedResults.tracking.created += result.tracking.created || 0;
      aggregatedResults.tracking.updated += result.tracking.updated || 0;
    }

    // Call progress callback
    if (onProgress) {
      onProgress({
        processed: aggregatedResults.processed,
        created: aggregatedResults.created,
        updated: aggregatedResults.updated,
        skipped: aggregatedResults.skipped,
        tracking: aggregatedResults.tracking,
      });
    }

    cursor = result.nextCursor;

    // Stop pagination if weekly limit was reached during this batch
    if (result.limitReachedDuringScrape) {
      console.log('Weekly limit reached during scrape — stopping pagination');
      break;
    }

    // Small delay between batches to avoid rate limiting
    if (cursor) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  } while (cursor);

  return aggregatedResults;
};

/**
 * Check shipping updates for specific orders
 * This is a convenience function for the toolbar action to check selected orders
 * @param {Object} options - Options
 * @param {string[]} options.orderNumbers - Order numbers to check for shipping updates
 * @param {string} [options.connectionId] - Optional specific email connection to use
 * @param {function} [options.onProgress] - Progress callback
 * @returns {Promise<Object>} - Results with updated orders
 */
export const checkShippingUpdates = async ({ orderNumbers, connectionId, onProgress }) => {
  if (!orderNumbers || orderNumbers.length === 0) {
    throw new Error('No orders selected to check');
  }

  // Use a wide date range (last 90 days) to catch shipping emails
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 90);

  return scrapeOrderEmails({
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
    scanMode: 'shipping',
    orderNumbers,
    connectionId,
    autoFetchTracking: false, // Not needed in shipping mode
    onProgress,
  });
};

/**
 * Format date for display
 */
export const formatDateRange = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  const options = { month: 'short', day: 'numeric' };

  if (start.getFullYear() !== end.getFullYear()) {
    return `${start.toLocaleDateString('en-US', { ...options, year: 'numeric' })} - ${end.toLocaleDateString('en-US', { ...options, year: 'numeric' })}`;
  }

  if (start.getMonth() === end.getMonth()) {
    return `${start.toLocaleDateString('en-US', { month: 'short' })} ${start.getDate()}-${end.getDate()}, ${start.getFullYear()}`;
  }

  return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}, ${start.getFullYear()}`;
};

/**
 * Get suggested date ranges for quick selection
 */
export const getSuggestedDateRanges = () => {
  const today = new Date();
  // Use local date formatting to avoid timezone issues (YYYY-MM-DD)
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Tomorrow (today + 1) — Gmail API uses timestamps, so endDate needs to be
  // the day after to capture all emails from the last day in the range
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  // Last 7 days
  const last7Days = new Date(today);
  last7Days.setDate(last7Days.getDate() - 7);

  // Last 30 days
  const last30Days = new Date(today);
  last30Days.setDate(last30Days.getDate() - 30);

  // This month
  const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);

  // Last month
  const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);

  return [
    {
      id: 'last7',
      label: 'Last 7 days',
      startDate: formatDate(last7Days),
      endDate: formatDate(tomorrow),
    },
    {
      id: 'last30',
      label: 'Last 30 days',
      startDate: formatDate(last30Days),
      endDate: formatDate(tomorrow),
    },
    {
      id: 'thisMonth',
      label: 'This month',
      startDate: formatDate(thisMonthStart),
      endDate: formatDate(tomorrow),
    },
    {
      id: 'lastMonth',
      label: 'Last month',
      startDate: formatDate(lastMonthStart),
      endDate: formatDate(lastMonthEnd),
    },
  ];
};

/**
 * Check if URL params indicate Gmail connection result
 * Security: Email is no longer passed in the URL — frontend fetches connections from DB
 */
export const checkGmailConnectionResult = () => {
  const params = new URLSearchParams(window.location.search);

  const connected = params.get('gmail_connected');
  const error = params.get('gmail_error');

  // Clean up URL params immediately
  if (connected || error) {
    const url = new URL(window.location.href);
    url.searchParams.delete('gmail_connected');
    url.searchParams.delete('email'); // Clean up legacy param if present
    url.searchParams.delete('gmail_error');
    window.history.replaceState({}, '', url.pathname + url.search);
  }

  if (connected) {
    // Success — email will be fetched from the database by the caller
    return { success: true };
  }

  if (error) {
    const errorMessages = {
      'missing_params': 'OAuth callback missing required parameters',
      'invalid_state': 'Invalid OAuth state - please try again',
      'state_expired': 'OAuth session expired - please try again',
      'token_exchange': 'Failed to exchange authorization code',
      'userinfo_failed': 'Failed to get Gmail account info',
      'no_email': 'Could not get email address from Google',
      'storage_failed': 'Failed to save Gmail connection',
      'server_config': 'Server configuration error',
      'access_denied': 'Access was denied - please try again',
    };

    return {
      success: false,
      error: errorMessages[error] || `OAuth error: ${error}`,
    };
  }

  return null;
};

// ============================================================================
// LEGACY LIMIT-REQUEST FUNCTIONS — REMOVED IN PHASE 4C CLEANUP (2026-05-30)
// ============================================================================
//
// getWeeklyOrderUsage, hasPendingLimitRequest, submitLimitRequest,
// getLimitRequestHistory all read the legacy weekly_order_usage /
// scrape_limit_requests tables which were dropped. In the new 2-tier
// world there's no admin-approval workflow for quota increases — Free
// users upgrade to Plus for unlimited.
//
// Active callers were already none (the only references were in the
// archived EmailScraperPage.backup.js). If you need the user's current
// weekly count from the frontend, use the useWeeklyOrdersTrackingUsage
// hook.

// ============================================================================
// IMAP CONNECTION SUPPORT
// ============================================================================

/**
 * IMAP provider presets with default server settings
 */
export const IMAP_PRESETS = [
  { id: 'gmail', name: 'Gmail', host: 'imap.gmail.com', port: 993 },
  { id: 'outlook', name: 'Outlook / Hotmail', host: 'outlook.office365.com', port: 993 },
  { id: 'yahoo', name: 'Yahoo Mail', host: 'imap.mail.yahoo.com', port: 993 },
  { id: 'icloud', name: 'iCloud Mail', host: 'imap.mail.me.com', port: 993 },
  { id: 'aol', name: 'AOL Mail', host: 'imap.aol.com', port: 993 },
  { id: 'custom', name: 'Custom IMAP Server', host: '', port: 993 },
];

/**
 * Connect an IMAP account (test connection + store credentials)
 */
export const connectImap = async ({ host, port, username, password, useTls = true, testOnly = false, label = null, force = false }) => {
  const session = await getFunctionAccessToken();

  const response = await supabase.functions.invoke('imap-connect', {
    headers: {
      Authorization: `Bearer ${session.access_token}`,
    },
    body: { host, port, username, password, useTls, testOnly, label, force },
  });

  if (response.error) {
    // The function's own message is the useful one here — it names the
    // actual cause (bad credentials, no INBOX, TLS failure) and tells
    // iCloud/Gmail/Yahoo users they need an app-specific password.
    const detail = await extractFunctionError(response.error);
    throw new Error(detail || response.error.message || 'Failed to connect IMAP account');
  }

  if (response.data?.duplicate_warning) {
    return response.data;
  }

  return response.data;
};